import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import Config from '@/config';
import ls, { ACCESS_TOKEN_KEY } from '@/util/localstorage';
import SignatureCapture from './SignatureCapture';
import DocumentViewer from './DocumentViewer';
import SigningSuccess from './SigningSuccess';
import SigningError from './SigningError';

const SAMPLE_PDF_URL =
  'https://gravy-docs.s3.us-east-1.amazonaws.com/sample-local-pdf.pdf';

const QA_TESTING_TOKEN = 'qa_testing';

interface DocumentItem {
  id: number;
  name: string;
  version_id: number;
  sort_order: number;
  download_url: string | null;
  download_url_expires_at: string | null;
  signed: boolean;
  locked: boolean;
}

interface DocumentsData {
  documents: DocumentItem[];
  all_signed: boolean;
  signed_count: number;
  total_count: number;
}

type FlowState = 'loading' | 'signature' | 'viewing' | 'success' | 'error';

function getQaTestDocuments(): DocumentsData {
  return {
    documents: [
      {
        id: 1,
        name: 'Employee Handbook',
        version_id: 1,
        sort_order: 1,
        download_url: SAMPLE_PDF_URL,
        download_url_expires_at: new Date(Date.now() + 3600000).toISOString(),
        signed: false,
        locked: false,
      },
      {
        id: 2,
        name: 'Mediation & Arbitration Agreement',
        version_id: 2,
        sort_order: 2,
        download_url: SAMPLE_PDF_URL,
        download_url_expires_at: new Date(Date.now() + 3600000).toISOString(),
        signed: false,
        locked: true,
      },
      {
        id: 3,
        name: 'Florida Employee Notice',
        version_id: 3,
        sort_order: 3,
        download_url: SAMPLE_PDF_URL,
        download_url_expires_at: new Date(Date.now() + 3600000).toISOString(),
        signed: false,
        locked: true,
      },
    ],
    all_signed: false,
    signed_count: 0,
    total_count: 3,
  };
}

const DocumentSigning: React.FC = () => {
  const [flowState, setFlowState] = useState<FlowState>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [canRetry, setCanRetry] = useState(false);
  const [documentsData, setDocumentsData] = useState<DocumentsData | null>(
    null,
  );
  const [currentDoc, setCurrentDoc] = useState<DocumentItem | null>(null);
  const [signatureBase64, setSignatureBase64] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const tokenRef = useRef<string | null>(null);
  const userIdRef = useRef<string | null>(null);
  const documentIdRef = useRef<number | null>(null);
  const isQaTestingRef = useRef(false);
  const impersonateWorkerIdRef = useRef<string | null>(null);

  const getBaseUrl = () => {
    return Config.BASE_RUBY_URL || 'https://api.v2.gravywork.com';
  };

  const getHeaders = () => {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${tokenRef.current}`,
      'Content-Type': 'application/json',
    };
    if (impersonateWorkerIdRef.current) {
      headers['X-Impersonate-Worker'] = impersonateWorkerIdRef.current;
    }
    return headers;
  };

  const getExternalParams = () => {
    return userIdRef.current ? `?user_id=${userIdRef.current}` : '';
  };

  const fetchDocuments = useCallback(async () => {
    if (isQaTestingRef.current) {
      // On first load, create test data. On subsequent calls, return current state.
      if (!documentsData) {
        const data = getQaTestDocuments();
        setDocumentsData(data);
        return data;
      }
      return documentsData;
    }

    const response = await axios.get(
      `${getBaseUrl()}/external/documents${getExternalParams()}`,
      {
        headers: getHeaders(),
      },
    );

    if (response.data.success) {
      setDocumentsData(response.data.data);
      return response.data.data as DocumentsData;
    }
    throw new Error('Failed to fetch documents');
  }, [documentsData]);

  const fetchDocumentDetail = useCallback(
    async (doc: DocumentItem): Promise<DocumentItem> => {
      if (isQaTestingRef.current) {
        return {
          ...doc,
          download_url: SAMPLE_PDF_URL,
          download_url_expires_at: new Date(Date.now() + 3600000).toISOString(),
        };
      }

      try {
        const response = await axios.get(
          `${getBaseUrl()}/external/documents/${doc.id}${getExternalParams()}`,
          { headers: getHeaders() },
        );

        if (response.data.success) {
          return {
            ...doc,
            download_url: response.data.data.download_url,
            download_url_expires_at: response.data.data.download_url_expires_at,
          };
        }
      } catch (err) {
        console.error(
          '[DocumentSigning] Failed to fetch document detail:',
          err,
        );
      }
      return doc;
    },
    [],
  );

  const findCurrentDocument = useCallback(
    (data: DocumentsData): DocumentItem | null => {
      const docId = documentIdRef.current;
      if (docId) {
        const match = data.documents.find(
          (d) => d.id === docId && !d.signed && !d.locked,
        );
        if (match) return match;
      }
      return data.documents.find((d) => !d.signed && !d.locked) || null;
    },
    [],
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const userId = params.get('user_id');
    const docId = params.get('document_id');
    const impersonateWorkerId = params.get('impersonate');

    // Impersonate mode: use admin token from localStorage + worker_id header
    // Usage: /document-signing?impersonate=<worker_id>&document_id=<doc_id>
    if (impersonateWorkerId) {
      const adminToken = ls.get(ACCESS_TOKEN_KEY);
      if (!adminToken) {
        setErrorMessage(
          'You must be logged into tenant-admin to use impersonate mode.',
        );
        setCanRetry(false);
        setFlowState('error');
        return;
      }
      tokenRef.current = adminToken;
      impersonateWorkerIdRef.current = impersonateWorkerId;
      documentIdRef.current = docId ? parseInt(docId, 10) : null;
      isQaTestingRef.current = false;
      loadDocuments();
      return;
    }

    if (!token) {
      setErrorMessage('Missing token. Please open this page from the app.');
      setCanRetry(false);
      setFlowState('error');
      return;
    }

    tokenRef.current = token;
    userIdRef.current = userId;
    documentIdRef.current = docId ? parseInt(docId, 10) : null;
    isQaTestingRef.current = token === QA_TESTING_TOKEN;

    // Strip token from URL
    if (!isQaTestingRef.current) {
      const cleanParams = new URLSearchParams();
      if (docId) cleanParams.set('document_id', docId);
      const source = params.get('source');
      if (source) cleanParams.set('source', source);
      const cleanUrl = `${window.location.pathname}?${cleanParams.toString()}`;
      window.history.replaceState({}, '', cleanUrl);
    }

    loadDocuments();
  }, []);

  const fetchDocumentUrl = useCallback(
    async (docId: number): Promise<string> => {
      if (isQaTestingRef.current) {
        return SAMPLE_PDF_URL;
      }

      const response = await axios.get(
        `${getBaseUrl()}/external/documents/${docId}${getExternalParams()}`,
        { headers: getHeaders() },
      );

      if (response.data.success && response.data.data.download_url) {
        return response.data.data.download_url;
      }
      throw new Error('Failed to fetch document URL');
    },
    [],
  );

  const loadDocuments = async () => {
    setFlowState('loading');
    try {
      const data = await fetchDocuments();

      if (data.all_signed) {
        setFlowState('success');
        return;
      }

      const doc = findCurrentDocument(data);
      if (!doc) {
        setFlowState('success');
        return;
      }

      const docWithUrl = await fetchDocumentDetail(doc);
      setCurrentDoc(docWithUrl);
      setFlowState('viewing');
    } catch (err: any) {
      if (err?.response?.status === 401) {
        setErrorMessage(
          'This link is no longer valid. Please go back to the app and try again.',
        );
        setCanRetry(false);
      } else {
        setErrorMessage(
          'Could not load documents. Check your connection and try again.',
        );
        setCanRetry(true);
      }
      setFlowState('error');
    }
  };

  const handleSignatureComplete = (base64: string) => {
    setSignatureBase64(base64);
    setFlowState('viewing');
  };

  const handleCaptureSignature = () => {
    setFlowState('signature');
  };

  const handleSign = async () => {
    if (!currentDoc || !signatureBase64 || submitting) return;

    setSubmitting(true);
    try {
      let freshData: DocumentsData;

      if (isQaTestingRef.current) {
        console.log(
          `[QA] Signed document ${currentDoc.id} (${currentDoc.name})`,
        );
        // Build updated state immutably
        const docs = documentsData?.documents ?? [];
        let foundCurrent = false;
        const updatedDocs = docs.map((d) => {
          if (d.id === currentDoc.id) {
            foundCurrent = true;
            return { ...d, signed: true };
          }
          if (foundCurrent && !d.signed && d.locked) {
            foundCurrent = false;
            return { ...d, locked: false };
          }
          return d;
        });
        const signedCount = updatedDocs.filter((d) => d.signed).length;
        freshData = {
          documents: updatedDocs,
          signed_count: signedCount,
          total_count: documentsData?.total_count ?? 0,
          all_signed: signedCount === (documentsData?.total_count ?? 0),
        };
        setDocumentsData(freshData);
      } else {
        const response = await axios.post(
          `${getBaseUrl()}/external/documents/${currentDoc.id}/sign${getExternalParams()}`,
          { signature_image: signatureBase64 },
          { headers: getHeaders() },
        );

        if (!response.data.success) {
          throw new Error(
            response.data.errors?.[0] || 'Failed to sign document',
          );
        }

        // Refetch from server to get updated list with next unlocked doc
        freshData = await fetchDocuments();
      }

      if (freshData.all_signed) {
        setFlowState('success');
        postMessageToApp({
          type: 'document_signing_complete',
          all_signed: true,
        });
        return;
      }

      const nextDoc = findCurrentDocument(freshData);

      postMessageToApp({
        type: 'document_signed',
        document_id: currentDoc.id,
        signed_count: freshData.signed_count,
        total_count: freshData.total_count,
        all_signed: false,
      });

      if (nextDoc) {
        const nextDocWithUrl = await fetchDocumentDetail(nextDoc);
        setCurrentDoc(nextDocWithUrl);
        documentIdRef.current = null;
      } else {
        setFlowState('success');
        postMessageToApp({
          type: 'document_signing_complete',
          all_signed: true,
        });
      }
    } catch (err: any) {
      console.error('[DocumentSigning] Sign error:', err);
      alert(
        err?.response?.data?.errors?.[0] ||
          err?.message ||
          'Could not submit signature. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDone = () => {
    postMessageToApp({
      type: 'document_signing_complete',
      all_signed: true,
    });
  };

  const postMessageToApp = (data: Record<string, unknown>) => {
    try {
      if ((window as any).ReactNativeWebView?.postMessage) {
        (window as any).ReactNativeWebView.postMessage(JSON.stringify(data));
      }
    } catch (e) {
      console.error('[DocumentSigning] postMessage error:', e);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: '#ffffff',
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        WebkitUserSelect: 'none',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      {flowState === 'loading' && <LoadingState />}

      {flowState === 'signature' && (
        <SignatureCapture
          onComplete={handleSignatureComplete}
          existingSignature={signatureBase64}
        />
      )}

      {flowState === 'viewing' && currentDoc && documentsData && (
        <DocumentViewer
          document={currentDoc}
          signatureBase64={signatureBase64}
          signedCount={documentsData.signed_count}
          totalCount={documentsData.total_count}
          onSign={handleSign}
          onCaptureSignature={handleCaptureSignature}
          submitting={submitting}
          fetchDocumentUrl={fetchDocumentUrl}
        />
      )}

      {flowState === 'success' && <SigningSuccess onDone={handleDone} />}

      {flowState === 'error' && (
        <SigningError
          message={errorMessage}
          canRetry={canRetry}
          onRetry={loadDocuments}
        />
      )}
    </div>
  );
};

const LoadingState: React.FC = () => (
  <div
    style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '16px',
    }}
  >
    <div
      style={{
        width: '40px',
        height: '40px',
        border: '3px solid #e5e5e5',
        borderTopColor: '#6366f1',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }}
    />
    <span style={{ fontSize: '15px', color: '#6b7280' }}>
      Loading documents...
    </span>
    <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
  </div>
);

export default DocumentSigning;
